import { Text } from '@village/ui';
import { useBookingSteps } from 'citymd/hooks';
import { useBooking, useCaptureCountlyEvent } from 'hooks';
import { FC, Fragment, PropsWithChildren, useEffect, useRef } from 'react';

import { StepTitle } from '../step-title';
import * as Styled from './styles';

const FMCSA_FORM_MCSA_5870_URL =
    'https://www.fmcsa.dot.gov/sites/fmcsa.dot.gov/files/2025-04/Insulin-Treated%20Diabetes%20Mellitus%20Assessment%20Form%20MCSA-5870_508.pdf';
const FMCSA_FORM_MCSA_5871_URL =
    'https://www.fmcsa.dot.gov/sites/fmcsa.dot.gov/files/2025-04/Vision%20Evaluation%20Report%20Form%20MCSA-5871_508.pdf';

const FormLink: FC<PropsWithChildren<{ href: string }>> = ({ href, children }) => (
    <Styled.Link href={href} target="_blank" rel="noopener noreferrer">
        {children}
    </Styled.Link>
);

const conditions = [
    {
        title: 'Insulin-Treated Diabetes',
        requirement: (
            <Fragment>
                Bring a completed <FormLink href={FMCSA_FORM_MCSA_5870_URL}>Form MCSA-5870</FormLink> signed by your treating
                physician within the last 45 days
            </Fragment>
        ),
    },
    {
        title: 'Vision/Monocular Vision',
        requirement: (
            <Fragment>
                Bring a completed <FormLink href={FMCSA_FORM_MCSA_5871_URL}>Form MCSA-5871</FormLink> signed by your eye doctor if
                you cannot pass the standard vision test in both eyes.
            </Fragment>
        ),
    },
    {
        title: 'Epilepsy or Seizures',
        requirement: 'Bring an official clearance letter from your neurologist that meets federal seizure-free guidelines.',
    },
    {
        title: 'Hearing Loss',
        requirement: 'Bring your hearing aids and functional batteries if you rely on them to pass a whisper test.',
    },
];

const DotPrescreening = () => {
    const { booking, setBookingField } = useBooking();
    const { goForward } = useBookingSteps();
    const { addCountlyEvent } = useCaptureCountlyEvent();
    const containerRef = useRef<HTMLDivElement>(null);

    const isAcknowledged = !!booking.dotPrescreeningAcknowledged;

    useEffect(() => {
        containerRef.current?.scrollIntoView({ block: 'start' });
    }, []);

    const handleContinue = () => {
        addCountlyEvent({ key: 'acknowledgeDotPrescreening' });
        goForward();
    };

    return (
        <Styled.Container ref={containerRef}>
            <Styled.DetailsContainer>
                <StepTitle>Before you book your DOT Physical</StepTitle>
                <Text type="body1">
                    The Federal Motor Carrier Safety Administration (FMCSA) requires specific paperwork <em>if</em> you have any
                    of the following health conditions.
                </Text>
                <Styled.WarningBanner status="warning" radius="extra">
                    <Text type="body1">
                        Missing documentation results in immediate exam failure, an official report to the National Registry, and
                        no refunds
                    </Text>
                </Styled.WarningBanner>
                <Text type="body1">Please review the list below to ensure you are prepared:</Text>
                {conditions.map(({ title, requirement }) => (
                    <Styled.Condition key={title}>
                        <Styled.ConditionTitle type="body1" fontWeight={700}>
                            {title}
                        </Styled.ConditionTitle>
                        <Styled.RequirementList>
                            <li>
                                <Text type="body1">{requirement}</Text>
                            </li>
                        </Styled.RequirementList>
                    </Styled.Condition>
                ))}
                <Styled.Checkbox
                    id="dot-prescreening-acknowledgement"
                    data-testid="dot-prescreening-acknowledgement"
                    checked={isAcknowledged}
                    $isChecked={isAcknowledged}
                    onChange={(_event, checked) => setBookingField({ dotPrescreeningAcknowledged: checked })}
                    label={
                        <Text type="body1">
                            I understand that if I have any of the conditions above, I must bring the required documentation or my
                            exam will be marked disqualified without a refund.
                        </Text>
                    }
                />
            </Styled.DetailsContainer>
            <Styled.ButtonWrapper>
                <Styled.Button onClick={handleContinue} disabled={!isAcknowledged} size="large">
                    Continue
                </Styled.Button>
            </Styled.ButtonWrapper>
        </Styled.Container>
    );
};

export { DotPrescreening };
